/*
 * Copyright 2026 Sony Corporation
 */

import type { AgentTurn, RequestOps } from './agent';
import type { SceneDigest } from '../scene-doc/digest';
import type { SceneOp, Vec3 } from '../scene-doc/schema';

/**
 * A scripted stand-in for the agent: canned ops for canned prompts.
 *
 * No model, no cost, no backend (devplan §12 U3). It exists to answer the two
 * questions worth asking this early — does a chat belong on this screen, and is
 * diff-then-confirm the right amount of ceremony — and it answers them with
 * real slugs against the real library, because a diff of plausible-looking
 * placeholders would settle neither.
 *
 * It does not pretend. A prompt it does not know is answered by saying so.
 */

/** Long enough that the thinking state is a real part of the design, not a flicker. */
const DEFAULT_DELAY_MS = 700;

const asset = (slug: string, name: string, position: Vec3): SceneOp => ({
  op: 'add_asset',
  source: 'polyhaven',
  slug,
  name,
  position,
});

/** Evenly spaced on a circle in the ground plane, starting on +x. */
const ring = (count: number, radius: number): Vec3[] =>
  Array.from({ length: count }, (_, index) => {
    const angle = (index / count) * Math.PI * 2;
    return [Math.cos(angle) * radius, 0, Math.sin(angle) * radius];
  });

const grid = (columns: number, rows: number, spacing: number): Vec3[] =>
  Array.from({ length: columns * rows }, (_, index) => {
    const column = index % columns;
    const row = Math.floor(index / columns);
    return [(column - (columns - 1) / 2) * spacing, 0, (row - (rows - 1) / 2) * spacing];
  });

/** The ground the scene opens with, so a texture has somewhere to land (devplan §5.1). */
const groundId = (digest: SceneDigest): string | undefined =>
  digest.nodes.find((node) => node.kind === 'primitive' && node.type === 'ground')?.id;

interface Script {
  /** Offered in the panel: what the stub knows is what the user is invited to ask. */
  prompt: string;
  matches: RegExp;
  reply: (digest: SceneDigest) => AgentTurn<SceneOp>;
}

const SCRIPTS: Script[] = [
  {
    prompt: 'Make a camp site in a forest — a fire pit, some seating, evening light',
    matches: /camp|forest|fire/i,
    reply: (digest) => {
      const ground = groundId(digest);
      return {
        text: 'Dusk forest lighting, a fire pit at the centre with four stumps around it, and a fallen trunk and boulder to break up the clearing.',
        ops: [
          { op: 'set_environment', slug: 'dark_autumn_forest' },
          ...(ground ? [{ op: 'set_material' as const, node_ids: [ground], slug: 'forest_ground_06' }] : []),
          asset('stone_fire_pit', 'Stone Fire Pit', [0, 0, 0]),
          ...ring(4, 1.8).map((position) => asset('tree_stump_01', 'Tree Stump', position)),
          asset('dead_tree_trunk', 'Dead Tree Trunk', [-3.4, 0, 1.9]),
          asset('boulder_01', 'Boulder', [2.9, 0, -2.6]),
        ],
      };
    },
  },
  {
    prompt: 'Set up a product shot — a table, a chair and three-point lighting',
    matches: /studio|product|three.?point|lighting/i,
    reply: () => ({
      text: 'A table and chair under a studio HDRI, with a key, a fill and a rim light so the subject has some shape to it.',
      ops: [
        { op: 'set_environment', slug: 'photo_studio_01', intensity: 0.6 },
        asset('dining_table', 'Dining Table', [0, 0, 0]),
        asset('GreenChair_01', 'Green Chair', [0, 0, 1.1]),
        { op: 'add_light', type: 'directional', intensity: 1.2, position: [3.5, 4, 3] },
        { op: 'add_light', type: 'point', intensity: 30, position: [-3, 2.5, 2] },
        { op: 'add_light', type: 'spot', intensity: 45, position: [0, 3.5, -3.5] },
      ],
    }),
  },
  {
    // Over the confirmation threshold on purpose: a batch this size is the case
    // the diff was built for, and the one the UX review has to judge.
    prompt: 'Lay out seating for a workshop — twenty-four chairs in rows',
    matches: /chairs|seating|workshop|audience|rows/i,
    reply: () => ({
      text: 'Twenty-four chairs in six rows of four, 0.9 m apart, centred on the origin.',
      ops: grid(4, 6, 0.9).map((position) => asset('GreenChair_01', 'Green Chair', position)),
    }),
  },
];

/** What this stub knows how to do, shown in the panel rather than left to be guessed. */
export const SCENE_STUB_PROMPTS: readonly string[] = SCRIPTS.map((script) => script.prompt);

const unknown: AgentTurn<SceneOp> = {
  text: `This is a scripted stand-in, not a model — there is no AI behind it yet. It answers three prompts:\n\n${SCENE_STUB_PROMPTS.map(
    (prompt) => `• ${prompt}`
  ).join('\n')}`,
  ops: [],
};

export const createAgentStub =
  (delayMs: number = DEFAULT_DELAY_MS): RequestOps<SceneOp, SceneDigest> =>
  async (prompt, digest) => {
    await new Promise((resolve) => setTimeout(resolve, delayMs));
    const script = SCRIPTS.find(({ matches }) => matches.test(prompt));
    return script ? script.reply(digest) : unknown;
  };
